import React from "react";
import { Card, Divider, Group, Stack, Text } from "@mantine/core";

// Shared read-only rendering for a pay deal's terms — used by the rep's Deal tab
// and by the Pay Details step of the add-deal flow, so both read the same.

export const ORANGE = "var(--mantine-color-orange-6)";

export const UPFRONT_PAY_TYPE_LABELS = {
  FIXED_AMOUNT_PER_ACCOUNT: "Fixed Amount Per Account",
  SCALE_AMOUNT_PER_ACCOUNT: "Scaled Amount Per Account",
  SCALE_AMOUNT_TOTAL: "Scaled Amount Total",
  FIXED_PERCENT_ACV: "Fixed Percentage of ACV",
  FIXED_PERCENTAGE_ACV: "Fixed Percentage of ACV",
  SCALE_PERCENT_ACV: "Scaled Percentage of ACV",
  INITIAL_PRICE: "Initial Price",
};

export const BONUS_CATEGORY_LABELS = {
  MONEY: "Cash Bonus",
  ITEM: "Item Bonus",
  RECURRING: "Recurring Bonus",
};

export const EXPENSE_CATEGORY_LABELS = {
  BACKEND_FIXED_AMOUNT: "Backend · Fixed Amount",
  BACKEND_PERCENTAGE: "Backend · Percentage",
  RECURRING_FRONTEND: "Recurring · Frontend",
  RECURRING_FRONTEND_AMOUNT: "Recurring · Frontend",
};

export const currency = value => {
  const n = Number(value) || 0;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export const percent = value => {
  const n = Number(value) || 0;
  return `${n === Math.round(n) ? n : n.toFixed(1)}%`;
};

export const titleize = value =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());

export const toScaleLines = lines =>
  (Array.isArray(lines) ? lines : []).map(l => ({
    revenue_amount: Number(l.revenue_amount) || 0,
    percentage: Number(l.percentage) || 0,
  }));

// Column headers + rows describing a deal user's upfront pay, by type.
export const upfrontConfig = up => {
  const scaleRows = isPercent =>
    (up.scale_lines || [])
      .slice()
      .sort((a, b) => (a.accounts_amount || 0) - (b.accounts_amount || 0))
      .map(line => {
        const accounts = Number(line.accounts_amount) || 0;
        return [
          `${accounts} account${accounts === 1 ? "" : "s"}`,
          isPercent ? percent(line.percentage) : currency(line.amount),
        ];
      });

  switch (up.upfront_pay_type) {
    case "FIXED_AMOUNT_PER_ACCOUNT":
      return {
        col1: "Per Account",
        col2: "Amount",
        rows: [["Each account", currency(up.fixed_amount)]],
      };
    case "FIXED_PERCENT_ACV":
    case "FIXED_PERCENTAGE_ACV":
      return {
        col1: "Basis",
        col2: "% of ACV",
        rows: [["Each deal", percent(up.fixed_percentage)]],
      };
    case "SCALE_AMOUNT_PER_ACCOUNT":
    case "SCALE_AMOUNT_TOTAL":
      return { col1: "Accounts", col2: "Amount", rows: scaleRows(false) };
    case "SCALE_PERCENT_ACV":
      return { col1: "Accounts", col2: "% of ACV", rows: scaleRows(true) };
    case "INITIAL_PRICE":
      return {
        col1: "Basis",
        col2: "Upfront",
        rows: [["Initial price", "Cost of initial service"]],
      };
    default:
      if (up.fixed_amount != null) {
        return {
          col1: "Per Account",
          col2: "Amount",
          rows: [["Each account", currency(up.fixed_amount)]],
        };
      }
      if (up.fixed_percentage != null) {
        return {
          col1: "Basis",
          col2: "Percentage",
          rows: [["Each deal", percent(up.fixed_percentage)]],
        };
      }
      return { col1: "Accounts", col2: "Amount", rows: scaleRows(false) };
  }
};

// What the rep must hit to earn a bonus, e.g. "$25,000 serviced revenue".
export const bonusThreshold = b => {
  const qualifier = [b.bonus_type_earned_by_type, b.bonus_type_earned_by_item]
    .filter(Boolean)
    .map(v => String(v).toLowerCase())
    .join(" ");
  switch (b.bonus_type_earned_by_item) {
    case "REVENUE":
      return `${currency(b.bonus_revenue_amount)} ${qualifier}`;
    case "ACCOUNTS":
      return `${Number(b.bonus_accounts_amount) || 0} ${qualifier}`;
    case "RECRUITS":
      return `${Number(b.bonus_recruits_amount) || 0} ${qualifier}`;
    default:
      return qualifier || "—";
  }
};

export const bonusRows = b => {
  const rows = [];
  if (b.bonus_type_category === "ITEM") {
    const value = b.item_monetary_value
      ? ` (${currency(b.item_monetary_value)})`
      : "";
    rows.push(["Item", `${b.item_name || "Item"}${value}`]);
  } else if (b.bonus_type_category === "RECURRING") {
    if (b.recurring_initial_amount != null) {
      rows.push(["Initial", currency(b.recurring_initial_amount)]);
    }
    if (b.recurring_repeated_amount != null) {
      const schedule = (b.recurring_schedule_type || "").toLowerCase();
      rows.push([
        "Recurring",
        schedule
          ? `${currency(b.recurring_repeated_amount)} ${schedule}`
          : currency(b.recurring_repeated_amount),
      ]);
    }
  } else if (b.bonus_amount != null) {
    rows.push(["Amount", currency(b.bonus_amount)]);
  }
  rows.push(["Earned when", bonusThreshold(b)]);
  return rows;
};

export const expenseRows = e => {
  const rows = [];
  if (e.backend_fixed_amount != null) {
    rows.push(["Amount", currency(e.backend_fixed_amount)]);
  }
  if (e.backend_percentage != null) {
    rows.push(["Percentage", percent(e.backend_percentage)]);
  }
  if (e.recurring_frontend_amount != null) {
    const schedule = (e.recurring_schedule_type || "").toLowerCase();
    rows.push([
      "Recurring",
      schedule
        ? `${currency(e.recurring_frontend_amount)} ${schedule}`
        : currency(e.recurring_frontend_amount),
    ]);
  }
  if (rows.length === 0) {
    rows.push([
      "Type",
      EXPENSE_CATEGORY_LABELS[e.expense_type_category] ||
        titleize(e.expense_type_category),
    ]);
  }
  return rows;
};

// A read-only two-column table of label/value rows.
export function DetailTable({ title, subtitle, col1Label, col2Label, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      <Stack gap={0} py="xs">
        <Text ta="center" fw={700} size="sm">
          {title}
        </Text>
        {subtitle && (
          <Text ta="center" size="xs" c="dimmed">
            {subtitle}
          </Text>
        )}
      </Stack>
      <Divider />
      <Group gap={0} style={{ background: "var(--mantine-color-gray-1)" }}>
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="xs"
          py={6}
          tt="uppercase"
        >
          {col1Label}
        </Text>
        <Divider orientation="vertical" />
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="xs"
          py={6}
          tt="uppercase"
        >
          {col2Label}
        </Text>
      </Group>
      {rows.map((row, i) => (
        <React.Fragment key={i}>
          <Divider />
          <Group gap={0}>
            <Text style={{ flex: 1 }} ta="center" size="sm" py={8}>
              {row[0]}
            </Text>
            <Divider orientation="vertical" />
            <Text style={{ flex: 1 }} ta="center" size="sm" fw={600} py={8}>
              {row[1]}
            </Text>
          </Group>
        </React.Fragment>
      ))}
    </Card>
  );
}

// A read-only revenue → percentage scale table (orange header, "Start" base row),
// matching the editable table used in the add-deal flow.
export function ScalePreviewTable({ title, subtitle, col2Label, lines }) {
  const rows = toScaleLines(lines)
    .slice()
    .sort((a, b) => a.revenue_amount - b.revenue_amount);

  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      <Stack gap={0} py="xs">
        <Text ta="center" fw={700} size="sm">
          {title}
        </Text>
        {subtitle && (
          <Text ta="center" size="xs" c="dimmed">
            {subtitle}
          </Text>
        )}
      </Stack>
      <Divider />
      <Group gap={0} style={{ background: ORANGE }}>
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="sm"
          py="xs"
          c="white"
        >
          Sales
        </Text>
        <Divider orientation="vertical" color="rgba(255,255,255,0.4)" />
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="sm"
          py="xs"
          c="white"
        >
          {col2Label}
        </Text>
      </Group>
      {rows.length === 0 ? (
        <>
          <Divider />
          <Text ta="center" size="sm" c="dimmed" py="sm">
            No scale set
          </Text>
        </>
      ) : (
        rows.map((line, i) => (
          <React.Fragment key={i}>
            <Divider />
            <Group
              gap={0}
              style={
                i === 0
                  ? { background: "var(--mantine-color-gray-1)" }
                  : undefined
              }
            >
              <Text
                style={{ flex: 1 }}
                ta="center"
                size="sm"
                fw={i === 0 ? 700 : 500}
                py={8}
              >
                {i === 0 ? "Start" : `> ${currency(line.revenue_amount)}`}
              </Text>
              <Divider orientation="vertical" />
              <Text style={{ flex: 1 }} ta="center" size="sm" fw={600} py={8}>
                {percent(line.percentage)}
              </Text>
            </Group>
          </React.Fragment>
        ))
      )}
    </Card>
  );
}
